import React from "react";
import { Button, Collapse, DatePicker, Form, Input, InputNumber, Spin, Table, Tag, Tooltip } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import { useLazyFetchManufacturingUsageReportQuery } from "features/store";

const { Panel } = Collapse;

const innerColumns = [
  { title: "Cap Side", dataIndex: "cap_side", key: "cap_side" },
  { title: "Material Type", dataIndex: "material_type", key: "material_type" },
  { title: "Material Name", dataIndex: "material_name", key: "material_name" },
  { title: "Color", dataIndex: "color", key: "color" },
  { title: "Qty Per Cap", dataIndex: "qty_per_cap", key: "qty_per_cap" },
  { title: "Total Used", dataIndex: "total_used", key: "total_used" },
];

const outerColumns = [
  { title: "Cap Type", dataIndex: "cap_type", key: "cap_type" },
  { title: "Manufacture Qty", dataIndex: "manufacture_quantity", key: "manufacture_quantity" },
  { title: "Date", dataIndex: "date", key: "date" },
];

const expandedRowRender = (record) => (
  <Table
    columns={innerColumns}
    dataSource={record.materials_used || []}
    rowKey={(_, i) => i}
    pagination={false}
    size="small"
  />
);

// Flattens outer + inner rows for CSV export
const flattenForCsv = (results) =>
  results.flatMap((run) => {
    const materials = run.materials_used || [];
    if (materials.length === 0) {
      return [{
        cap_type: run.cap_type,
        manufacture_quantity: run.manufacture_quantity,
        date: run.date,
        cap_side: "",
        material_type: "",
        material_name: "",
        color: "",
        qty_per_cap: "",
        total_used: "",
      }];
    }
    return materials.map((m) => ({
      cap_type: run.cap_type,
      manufacture_quantity: run.manufacture_quantity,
      date: run.date,
      cap_side: m.cap_side,
      material_type: m.material_type,
      material_name: m.material_name,
      color: m.color,
      qty_per_cap: m.qty_per_cap,
      total_used: m.total_used,
    }));
  });

const csvHeaders = [
  { label: "Cap Type", key: "cap_type" },
  { label: "Manufacture Qty", key: "manufacture_quantity" },
  { label: "Date", key: "date" },
  { label: "Cap Side", key: "cap_side" },
  { label: "Material Type", key: "material_type" },
  { label: "Material Name", key: "material_name" },
  { label: "Color", key: "color" },
  { label: "Qty Per Cap", key: "qty_per_cap" },
  { label: "Total Used", key: "total_used" },
];

const InvRepManufacturing = () => {
  const [form] = Form.useForm();
  const [trigger, { data, isLoading, isFetching }] = useLazyFetchManufacturingUsageReportQuery();

  const handleApply = () => {
    const v = form.getFieldsValue();
    const params = {};
    if (v.cap_type) params.cap_type = v.cap_type;
    if (v.date_from) params.date_from = v.date_from.format("YYYY-MM-DD");
    if (v.date_to) params.date_to = v.date_to.format("YYYY-MM-DD");
    if (v.min_quantity != null) params.min_quantity = v.min_quantity;
    if (v.max_quantity != null) params.max_quantity = v.max_quantity;
    trigger(params);
  };

  const handleClear = () => {
    form.resetFields();
    trigger({});
  };

  const results = data?.results || [];
  const count = data?.count ?? results.length;

  return (
    <div style={{ padding: "24px" }}>
      <h2 style={{ marginBottom: 16 }}>Manufacturing Usage Report</h2>

      <Collapse defaultActiveKey={["filters"]} style={{ marginBottom: 16 }}>
        <Panel header="Filters" key="filters">
          <Form form={form} layout="inline" style={{ flexWrap: "wrap", gap: "8px 0" }}>
            <Form.Item name="cap_type" label="Cap Type">
              <Input placeholder="e.g. BUCKET HAT" style={{ width: 160 }} />
            </Form.Item>
            <Form.Item name="date_from" label="Date From">
              <DatePicker />
            </Form.Item>
            <Form.Item name="date_to" label="Date To">
              <DatePicker />
            </Form.Item>
            <Form.Item name="min_quantity" label="Min Qty">
              <InputNumber min={0} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item name="max_quantity" label="Max Qty">
              <InputNumber min={0} style={{ width: 100 }} />
            </Form.Item>
          </Form>
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <Button type="primary" onClick={handleApply} loading={isLoading || isFetching}>
              Apply Filters
            </Button>
            <Button onClick={handleClear}>Clear Filters</Button>
          </div>
        </Panel>
      </Collapse>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        {data && (
          <Tag color="blue" style={{ fontSize: 14 }}>Total Runs: {count}</Tag>
        )}
        {results.length > 0 && (
          <Tooltip title="Export to Excel (flattened — one row per material used)">
            <CSVLink
              data={flattenForCsv(results)}
              headers={csvHeaders}
              filename="manufacturing-usage-report.csv"
            >
              <Button icon={<FileExcelOutlined />} style={{ color: "#217346", borderColor: "#217346" }}>
                Export to Excel
              </Button>
            </CSVLink>
          </Tooltip>
        )}
      </div>

      <Spin spinning={isLoading || isFetching}>
        <Table
          columns={outerColumns}
          dataSource={results}
          rowKey={(r, i) => r.id ?? i}
          expandable={{ expandedRowRender }}
          locale={{ emptyText: "No records found" }}
          scroll={{ x: true }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Spin>
    </div>
  );
};

export default InvRepManufacturing;
